import { useEffect, useRef, useState, useCallback } from 'react'
import { cn } from '@/utils/cn'
import type { Quaternion } from '@/utils/quaternion'
import { quaternionMultiply, quaternionFromAxisAngle, quaternionNormalize } from '@/utils/quaternion'

interface RotationGizmoProps {
  className?: string
  quaternion: Quaternion
  onChange: (quaternion: Quaternion) => void
  size?: number
}

export function RotationGizmo({ className, quaternion, onChange, size = 200 }: RotationGizmoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragAxis, setDragAxis] = useState<'x' | 'y' | 'z' | 'free' | null>(null)
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 })
  
  // Render the gizmo
  const render = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Clear canvas
    ctx.clearRect(0, 0, size, size)
    
    const center = size / 2
    const radius = size * 0.4
    
    // Background circle
    ctx.globalAlpha = 0.1
    ctx.beginPath()
    ctx.arc(center, center, radius, 0, Math.PI * 2)
    ctx.fillStyle = '#000'
    ctx.fill()
    
    // Draw rotation rings for each axis
    const axes = [
      { axis: 'x', color: '#ff4444', angle: Math.PI / 2 },
      { axis: 'y', color: '#44ff44', angle: 0 },
      { axis: 'z', color: '#4444ff', angle: Math.PI / 2 }
    ]
    
    axes.forEach(({ axis, color, angle }) => {
      ctx.save()
      ctx.translate(center, center)
      
      // Apply 3D rotation for perspective
      const scale = axis === 'y' ? 1 : Math.cos(angle) * 0.6 + 0.4
      
      ctx.globalAlpha = isDragging && dragAxis === axis ? 1 : 0.7
      ctx.strokeStyle = color
      ctx.lineWidth = isDragging && dragAxis === axis ? 4 : 3
      
      ctx.beginPath()
      if (axis === 'y') {
        // Y axis - full circle
        ctx.arc(0, 0, radius, 0, Math.PI * 2)
      } else if (axis === 'x') {
        // X axis - horizontal ellipse
        ctx.ellipse(0, 0, radius, radius * scale, 0, 0, Math.PI * 2)
      } else {
        // Z axis - vertical ellipse
        ctx.ellipse(0, 0, radius * scale, radius, 0, 0, Math.PI * 2)
      }
      ctx.stroke()
      
      ctx.restore()
    })
    
    // Draw center sphere
    ctx.globalAlpha = isDragging && dragAxis === 'free' ? 1 : 0.5
    ctx.beginPath()
    ctx.arc(center, center, 20, 0, Math.PI * 2)
    ctx.fillStyle = '#666'
    ctx.fill()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2
    ctx.stroke()
    
    // Draw axis labels
    ctx.globalAlpha = 1
    ctx.font = 'bold 12px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    
    // X label
    ctx.fillStyle = '#ff4444'
    ctx.fillText('X', center + radius + 15, center)
    
    // Y label
    ctx.fillStyle = '#44ff44'
    ctx.fillText('Y', center, center - radius - 15)
    
    // Z label
    ctx.fillStyle = '#4444ff'
    ctx.fillText('Z', center - radius - 15, center)
  }, [size, isDragging, dragAxis])
  
  useEffect(() => {
    render()
  }, [render, quaternion])
  
  // Detect which axis is being hovered/clicked
  const getAxisFromPoint = (x: number, y: number): 'x' | 'y' | 'z' | 'free' | null => {
    const center = size / 2
    const radius = size * 0.4
    const dx = x - center
    const dy = y - center
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    // Check center sphere for free rotation
    if (dist < 20) {
      return 'free'
    }
    
    // Check if near any ring (simplified hit detection)
    if (Math.abs(dist - radius) < 10) {
      // Determine which ring based on position
      
      // Y ring is always circular
      if (Math.abs(dist - radius) < 5) {
        return 'y'
      }
      
      // X ring is horizontal ellipse - stronger at sides
      if (Math.abs(dx) > Math.abs(dy) * 1.5) {
        return 'x'
      }
      
      // Z ring is vertical ellipse - stronger at top/bottom
      if (Math.abs(dy) > Math.abs(dx) * 1.5) {
        return 'z'
      }
      
      return 'y' // Default to Y for ambiguous cases
    }
    
    return null
  }
  
  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const axis = getAxisFromPoint(x, y)
    
    if (axis) {
      setIsDragging(true)
      setDragAxis(axis)
      setLastMousePos({ x: e.clientX, y: e.clientY })
      e.preventDefault()
    }
  }
  
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragAxis) return
    
    const deltaX = e.clientX - lastMousePos.x
    const deltaY = e.clientY - lastMousePos.y
    
    let rotationAxis: [number, number, number]
    let angle = 0
    
    if (dragAxis === 'free') {
      // Free rotation - combine X and Y rotations
      const sensitivity = 0.01
      
      // Rotate around Y axis based on horizontal movement
      if (Math.abs(deltaX) > 0) {
        const yRotation = quaternionFromAxisAngle([0, 1, 0], -deltaX * sensitivity)
        onChange(quaternionNormalize(quaternionMultiply(yRotation, quaternion)))
      }
      
      // Rotate around X axis based on vertical movement
      if (Math.abs(deltaY) > 0) {
        const xRotation = quaternionFromAxisAngle([1, 0, 0], -deltaY * sensitivity)
        onChange(quaternionNormalize(quaternionMultiply(xRotation, quaternion)))
      }
    } else {
      // Single axis rotation
      const sensitivity = 0.01
      
      switch (dragAxis) {
        case 'x':
          rotationAxis = [1, 0, 0]
          angle = -deltaY * sensitivity
          break
        case 'y':
          rotationAxis = [0, 1, 0]
          angle = -deltaX * sensitivity
          break
        case 'z':
          rotationAxis = [0, 0, 1]
          // Use combination of X and Y movement for Z rotation
          angle = (deltaX - deltaY) * sensitivity * 0.7
          break
      }
      
      if (angle !== 0) {
        const rotation = quaternionFromAxisAngle(rotationAxis!, angle)
        onChange(quaternionNormalize(quaternionMultiply(rotation, quaternion)))
      }
    }
    
    setLastMousePos({ x: e.clientX, y: e.clientY })
  }, [isDragging, dragAxis, lastMousePos, quaternion, onChange])
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setDragAxis(null)
  }, [])
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])
  
  // Handle hover effects
  const handleMouseMoveCanvas = (e: React.MouseEvent) => {
    if (isDragging) return
    
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const axis = getAxisFromPoint(x, y)
    
    // Update cursor based on hover
    if (canvasRef.current) {
      canvasRef.current.style.cursor = axis ? 'grab' : 'default'
    }
  }
  
  return (
    <div className={cn('relative', className)}>
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className="border border-gray-200 rounded-lg bg-white"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveCanvas}
        style={{ width: size, height: size }}
      />
      <div className="mt-2 text-xs text-gray-500 text-center">
        Drag rings to rotate • Drag center for free rotation
      </div>
    </div>
  )
}